import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Flask, EnvelopeSimple, CalendarPlus, ArrowCounterClockwise, Play, IdentificationBadge, Cake } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const OUTCOME_STYLE = (o) => {
  if (o?.startsWith("AUTO-DECLINED") || o?.startsWith("REJECTED")) return "bg-red-100 text-red-700";
  if (o?.includes("ACKNOWLEDGMENT")) return "bg-amber-100 text-amber-800";
  return "bg-emerald-100 text-emerald-800";
};

export default function SandboxPage() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [sim, setSim] = useState({ user_id: "", type: "pto", start_date: "", end_date: "" });
  const [simResult, setSimResult] = useState(null);
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/sandbox/state").then((r) => setState(r.data)).catch(() => {});
  }, []);

  if (user?.role !== "super_admin") return <Navigate to="/" replace />;

  const runSim = async () => {
    setBusy("sim");
    try {
      const { data } = await api.post("/sandbox/simulate-timeoff", sim);
      setSimResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Simulation failed");
    } finally {
      setBusy("");
    }
  };

  const runTest = async (key, path) => {
    setBusy(key);
    try {
      const { data } = await api.post(path);
      setResults((r) => ({ ...r, [key]: data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Test failed");
    } finally {
      setBusy("");
    }
  };

  if (!state) return <p className="text-sm text-muted-foreground">Loading sandbox…</p>;

  return (
    <div className="space-y-8">
      <div>
        <p className="uppercase tracking-[0.2em] text-xs font-semibold text-indigo-600 mb-2">Testing · Super admin only</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3"><Flask size={30} weight="duotone" className="text-indigo-600" /> Sandbox</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Dry-run every policy and integration without touching real data. Nothing here creates real requests or changes balances. Policy and notification settings now live on the <strong>Settings</strong> page.</p>
      </div>

      <div className="bg-card border border-border rounded-md p-6 space-y-4" data-testid="timeoff-simulator-card">
        <h3 className="font-heading font-semibold tracking-tight">Time-off request simulator</h3>
        <p className="text-xs text-muted-foreground">Runs the real auto-decline & short-notice rules as a dry run — no request is created, no email sent.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs">Member</Label>
            <Select value={sim.user_id} onValueChange={(v) => setSim({ ...sim, user_id: v })}>
              <SelectTrigger data-testid="sim-member-select" className="mt-1.5"><SelectValue placeholder="Pick member" /></SelectTrigger>
              <SelectContent>{state.members.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name} {m.department ? `(${m.department})` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={sim.type} onValueChange={(v) => setSim({ ...sim, type: v })}>
              <SelectTrigger data-testid="sim-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="pto">PTO</SelectItem><SelectItem value="sick">Sick</SelectItem><SelectItem value="unpaid">Unpaid</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Start date</Label><Input data-testid="sim-start-date" type="date" className="mt-1.5" value={sim.start_date} onChange={(e) => setSim({ ...sim, start_date: e.target.value })} /></div>
          <div><Label className="text-xs">End date</Label><Input data-testid="sim-end-date" type="date" className="mt-1.5" value={sim.end_date} onChange={(e) => setSim({ ...sim, end_date: e.target.value })} /></div>
        </div>
        <Button data-testid="run-sim-btn" onClick={runSim} disabled={!sim.user_id || !sim.start_date || !sim.end_date || busy === "sim"}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"><Play size={15} className="mr-1.5" /> {busy === "sim" ? "Running…" : "Run simulation"}</Button>
        {simResult && (
          <div className="rounded-md border border-border bg-muted/50 p-4 space-y-2 text-sm" data-testid="sim-result">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={OUTCOME_STYLE(simResult.outcome)}>{simResult.outcome}</Badge>
              <span className="text-muted-foreground text-xs">{simResult.member} {simResult.department ? `· ${simResult.department}` : ""} · {simResult.hours}h</span>
            </div>
            <p>{simResult.explanation}</p>
            {simResult.decision?.reasons?.map((r, i) => <p key={i} className="text-red-700 text-xs">• {r}</p>)}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-md p-6 space-y-3" data-testid="test-email-card">
          <div className="flex items-center gap-2"><EnvelopeSimple size={19} weight="duotone" className="text-indigo-600" /><h3 className="font-heading font-semibold tracking-tight text-sm">Email notifications</h3></div>
          <p className="text-xs text-muted-foreground">Sends a test notification to your own email.</p>
          <Button data-testid="test-email-btn" size="sm" variant="outline" onClick={() => runTest("email", "/sandbox/test-email")} disabled={busy === "email"}>{busy === "email" ? "Sending…" : "Send test email"}</Button>
          {results.email && (
            <div className="text-xs space-y-1">
              <Badge className={results.email.sent ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}>{results.email.sent ? "delivered" : "not delivered"}</Badge>
              <p className="text-muted-foreground">{results.email.sent ? `Sent to ${results.email.recipient}` : results.email.note}</p>
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-md p-6 space-y-3" data-testid="test-calendar-card">
          <div className="flex items-center gap-2"><CalendarPlus size={19} weight="duotone" className="text-indigo-600" /><h3 className="font-heading font-semibold tracking-tight text-sm">Team calendar sync</h3></div>
          <Badge className={state.calendar_configured ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{state.calendar_configured ? "Google Calendar connected" : "MOCK mode — not connected"}</Badge>
          <p className="text-xs text-muted-foreground">{state.calendar_configured ? "Creates and removes a test event on the team calendar." : "Approved time off is tracked in-app. Connect the Google service account to sync for real."}</p>
          <Button data-testid="test-calendar-btn" size="sm" variant="outline" onClick={() => runTest("calendar", "/sandbox/test-calendar")} disabled={busy === "calendar"}>{busy === "calendar" ? "Testing…" : "Test calendar"}</Button>
          {results.calendar && <p className="text-xs text-muted-foreground">{results.calendar.note}</p>}
        </div>

        <div className="bg-card border border-border rounded-md p-6 space-y-3" data-testid="year-reset-card">
          <div className="flex items-center gap-2"><ArrowCounterClockwise size={19} weight="duotone" className="text-indigo-600" /><h3 className="font-heading font-semibold tracking-tight text-sm">Yearly sick reset</h3></div>
          <p className="text-xs text-muted-foreground">Sick balances reset to {state.settings?.sick_annual_hours}h automatically each fiscal year (June 1).</p>
          <Button data-testid="test-year-reset-btn" size="sm" variant="outline" onClick={() => runTest("reset", "/sandbox/simulate-year-reset")} disabled={busy === "reset"}>{busy === "reset" ? "Checking…" : "Simulate reset"}</Button>
          {results.reset && (
            <div className="text-xs space-y-1 text-muted-foreground">
              <p>Last reset fiscal year: <strong>{results.reset.last_reset_fy}</strong> · Would reset now: <strong>{results.reset.would_reset_now ? "yes" : "no"}</strong></p>
              <p>{results.reset.on_reset}</p>
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-md p-6 space-y-3" data-testid="license-checks-card">
          <div className="flex items-center gap-2"><IdentificationBadge size={19} weight="duotone" className="text-indigo-600" /><h3 className="font-heading font-semibold tracking-tight text-sm">License expiration job</h3></div>
          <p className="text-xs text-muted-foreground">Runs the real daily job now: 90/60/30-day member reminders + 10-day admin alert digest. Scheduled daily at 13:00 UTC.</p>
          <Button data-testid="run-license-checks-btn" size="sm" variant="outline" onClick={() => runTest("licenses", "/sandbox/run-license-checks")} disabled={busy === "licenses"}>{busy === "licenses" ? "Running…" : "Run license checks now"}</Button>
          {results.licenses && (
            <div className="text-xs space-y-1 text-muted-foreground">
              <p>{results.licenses.note}</p>
              {results.licenses.admin_alerts?.map((a, i) => <p key={i} className="text-red-700">• {a.user} — {a.state}, {a.days_left < 0 ? "EXPIRED" : `${a.days_left}d left`}</p>)}
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-md p-6 space-y-3" data-testid="people-ops-card">
          <div className="flex items-center gap-2"><Cake size={19} weight="duotone" className="text-indigo-600" /><h3 className="font-heading font-semibold tracking-tight text-sm">Milestones & reviews job</h3></div>
          <p className="text-xs text-muted-foreground">Generates upcoming anniversary milestones + hire-anniversary review cycles (super admins excluded), then emails daily digests to assigned reviewers. Scheduled daily at 13:10 UTC.</p>
          <Button data-testid="run-people-ops-btn" size="sm" variant="outline" onClick={() => runTest("peopleops", "/sandbox/run-people-ops")} disabled={busy === "peopleops"}>{busy === "peopleops" ? "Running…" : "Run people-ops now"}</Button>
          {results.peopleops && <p className="text-xs text-muted-foreground">{results.peopleops.note}</p>}
        </div>
      </div>
    </div>
  );
}
